import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'hooks/use-redux'
import type { ClientLibrary, ClientLibraryId } from 'types/client-library'
import type { State } from 'types/redux'
import { clientLibraryUpdate } from '@disco/state-management/actions/client-library-list-actions'
import { getClientLibrary } from '@disco/state-management/selectors/client-libraries'
import { keyEqualTo } from '@disco/utils/keyboard'
import { linkify } from '@disco/utils/links'
import Row from 'components/common/row'
import HelpTooltip from 'components/common/tooltip'
import DocumentHeadTags from 'components/document-head-tags'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import PageContainer from 'components/ui/page-container'
import TextField from 'components/ui/text-field'
import { useFormState } from 'hooks/use-form-state'
const FORM_FIELDS = ['contact_info', 'contact_details', 'contact_website']

// Adds "http://" prefix to links when missing
const formatLinks = (value: string, cursor: number) => {
  const matches = linkify.match(value)

  if (!matches) {
    return {
      value,
      cursor,
    }
  }

  let result: string = ''
  let index: number = 0
  let cursorShift: number = 0
  matches.forEach(match => {
    if (match.schema) {
      result += value.slice(index, match.lastIndex)
    } else {
      if (cursor > match.index) {
        cursorShift += match.url.length - match.text.length
      }

      if (index < match.index) {
        result += value.slice(index, match.index)
      }

      result += match.url
    }

    index = match.lastIndex
  })
  result += value.slice(index)
  return {
    value: result,
    cursor: cursor + cursorShift,
  }
}

const LibrariesLibraryContact = (): React.ReactNode => {
  const params = useParams<{ libraryId: string }>()
  const libraryId: ClientLibraryId = parseInt(params.libraryId!, 10)
  const dispatch = useDispatch()
  const library: ClientLibrary = useSelector((state: State) =>
    getClientLibrary(state, libraryId),
  )
  const [formState, setFormState, pendingChanges] = useFormState(
    library,
    FORM_FIELDS,
  )
  const [shouldFormatLinks, setShouldFormatLinks] = useState({})

  const handleBlur = event => {
    const { name, selectionStart, value } = event.target
    const formatted = formatLinks(value, selectionStart)
    setFormState(name, formatted.value)
  }

  const handleChange = event => {
    const { name, selectionStart, value } = event.target

    if (shouldFormatLinks[name]) {
      const input = event.target
      const formatted = formatLinks(value, selectionStart)
      setFormState(name, formatted.value, () => {
        input.setSelectionRange(formatted.cursor, formatted.cursor)
      })
      setShouldFormatLinks({ ...shouldFormatLinks, [name]: false })
    } else {
      setFormState(name, value)
    }
  }

  const handleKeyPress = event => {
    if (keyEqualTo(event, 'Enter') || keyEqualTo(event, ' ')) {
      const { name } = event.target
      setShouldFormatLinks({ ...shouldFormatLinks, [name]: true })
    }
  }

  const handlePaste = event => {
    const { name } = event.target
    setShouldFormatLinks({ ...shouldFormatLinks, [name]: true })
  }

  const handleSubmit = (event: React.SyntheticEvent<any>) => {
    event.preventDefault()
    dispatch(clientLibraryUpdate(libraryId, formState))
  }

  return (
    <PageContainer>
      <PageContainer.Section
        title={
          <>
            Contact page
            <HelpTooltip>
              This information will appear on your Contact page on the client
              library.
            </HelpTooltip>
          </>
        }
      >
        {library && !library['loading'] && (
          <DocumentHeadTags
            title={`${library['name']} | Contact page | DISCO`}
          />
        )}
        <Row columnsSize={[6]}>
          <div>
            <TextField
              disabled={library && library['loading']}
              label="About"
              multiline={5}
              name="contact_info"
              placeholder="Enter information about you or your company here."
              value={formState['contact_info']}
              onBlur={handleBlur}
              onChange={handleChange}
              onKeyPress={handleKeyPress}
              onPaste={handlePaste}
            />
          </div>
        </Row>
        <Row columnsSize={[6]}>
          <div>
            <TextField
              disabled={library && library['loading']}
              label="Contact details"
              multiline={5}
              name="contact_details"
              placeholder="Enter your contact details here."
              value={formState['contact_details']}
              onBlur={handleBlur}
              onChange={handleChange}
              onKeyPress={handleKeyPress}
              onPaste={handlePaste}
            />
          </div>
        </Row>
        <Row columnsSize={[6]}>
          <div>
            <TextField
              disabled={library && library['loading']}
              label="Company website"
              name="contact_website"
              placeholder="www."
              value={formState['contact_website']}
              onBlur={handleBlur}
              onChange={handleChange}
              onKeyPress={handleKeyPress}
              onPaste={handlePaste}
            />
          </div>
        </Row>
        <PageContainer.Footer inline>
          <ButtonContainer>
            <Button
              disabled={!library || library?.loading || !pendingChanges}
              appearance="primary"
              caption="Save"
              onClick={handleSubmit}
            />
          </ButtonContainer>
        </PageContainer.Footer>
      </PageContainer.Section>
    </PageContainer>
  )
}

export default LibrariesLibraryContact
